import { useEffect, useState } from 'react'
import { Icon } from '../components/ui/Icons'
import PasswordField from '../components/ui/PasswordField'
import { cambiarEstadoUsuario, crearUsuarioDirectiva, listarUsuarios, restablecerPasswordUsuario } from '../services/usuariosService'

const MIN_PASSWORD = 8
const roleLabels = { admin: 'Administrador', directiva: 'Directiva' }
const inputClass = 'w-full rounded-xl border border-border bg-background px-4 py-3 outline-none transition focus:border-secondary focus:ring-4 focus:ring-secondary/15'
const dateTime = new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const dateOnly = new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })

function formatDate(value, withTime = false) {
  if (!value) return '—'
  return (withTime ? dateTime : dateOnly).format(new Date(value))
}

// Estado de la contraseña: nunca se muestra la contraseña, solo si está pendiente de cambio.
function ClaveBadge({ pendiente }) {
  return pendiente
    ? <span className="inline-flex whitespace-nowrap rounded-full bg-pending/10 px-2.5 py-1 text-xs font-semibold text-pending">Cambio de clave pendiente</span>
    : <span className="text-sm text-text-secondary">Definida</span>
}

function EstadoBadge({ activo }) {
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${activo ? 'border border-success/30 bg-success/10 text-success-text' : 'bg-danger/10 text-danger'}`}>{activo ? 'Activo' : 'Inactivo'}</span>
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center overflow-y-auto overscroll-contain bg-slate-950/60 px-3 py-safe-page sm:px-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="w-full max-w-md rounded-2xl bg-surface shadow-2xl" role="dialog" aria-modal="true" aria-label={title}>
        <header className="flex items-center justify-between gap-3 border-b border-border py-3 pl-4 pr-2 sm:py-4 sm:pl-5 sm:pr-3">
          <h3 className="min-w-0 text-lg font-bold sm:text-xl">{title}</h3>
          <button type="button" onClick={onClose} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-text-secondary hover:bg-background" aria-label="Cerrar"><Icon name="close" /></button>
        </header>
        {children}
      </section>
    </div>
  )
}

function passwordError(password, confirmation) {
  if (password.length < MIN_PASSWORD) return `La contraseña debe tener al menos ${MIN_PASSWORD} caracteres.`
  if (password !== confirmation) return 'Las contraseñas no coinciden.'
  return ''
}

function FormActions({ onClose, saving, label }) {
  return (
    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
      <button type="button" onClick={onClose} className="min-h-12 rounded-xl border border-border px-4 py-3 font-semibold">Cancelar</button>
      <button disabled={saving} className="min-h-12 rounded-xl bg-primary px-5 py-3 font-semibold text-white disabled:opacity-60">{saving ? 'Guardando…' : label}</button>
    </div>
  )
}

function NuevoUsuarioModal({ onClose, onCreated }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit(event) {
    event.preventDefault()
    const validation = passwordError(password, confirmation)
    if (validation) return setError(validation)
    setSaving(true); setError('')
    try {
      const usuario = await crearUsuarioDirectiva(email.trim(), password)
      onCreated(usuario)
    } catch (createError) {
      setError(createError.message)
      setSaving(false)
    }
  }

  return (
    <Modal title="Nuevo usuario" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4 p-4 sm:p-5" autoComplete="off">
        <label className="block text-sm font-medium text-text-primary"><span>Correo electrónico</span><input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" autoCapitalize="none" spellCheck="false" placeholder="nombre@correo.cl" className={inputClass + ' mt-1.5'} autoFocus /></label>
        <PasswordField label="Contraseña temporal" value={password} onChange={setPassword} />
        <PasswordField label="Confirmar contraseña temporal" value={confirmation} onChange={setConfirmation} />
        <div className="rounded-xl bg-background px-4 py-3 text-sm"><span className="text-text-secondary">Rol: </span><strong>Directiva</strong></div>
        <p className="text-xs text-text-secondary">Mínimo {MIN_PASSWORD} caracteres. El correo queda confirmado y, en su primer ingreso, el usuario deberá reemplazar esta contraseña temporal por una propia.</p>
        {error && <div className="rounded-xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{error}</div>}
        <FormActions onClose={onClose} saving={saving} label="Crear usuario" />
      </form>
    </Modal>
  )
}

function RestablecerPasswordModal({ usuario, onClose, onDone }) {
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit(event) {
    event.preventDefault()
    const validation = passwordError(password, confirmation)
    if (validation) return setError(validation)
    setSaving(true); setError('')
    try {
      await restablecerPasswordUsuario(usuario.id, password)
      onDone()
    } catch (changeError) {
      setError(changeError.message)
      setSaving(false)
    }
  }

  return (
    <Modal title="Restablecer contraseña" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4 p-4 sm:p-5" autoComplete="off">
        <p className="break-words text-sm text-text-secondary">Cuenta: <strong className="text-text-primary">{usuario.email}</strong></p>
        <PasswordField label="Contraseña temporal" value={password} onChange={setPassword} autoFocus />
        <PasswordField label="Confirmar contraseña temporal" value={confirmation} onChange={setConfirmation} />
        <p className="text-xs text-text-secondary">El usuario deberá cambiarla por una propia en su próximo ingreso.</p>
        {error && <div className="rounded-xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{error}</div>}
        <FormActions onClose={onClose} saving={saving} label="Restablecer contraseña" />
      </form>
    </Modal>
  )
}

function Acciones({ usuario, busy, onToggle, onPassword }) {
  if (usuario.propio) return <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">Tu cuenta</span>
  if (!usuario.gestionable) return <span className="text-xs text-text-secondary">—</span>
  return (
    <div className="flex flex-wrap gap-2 lg:justify-end">
      <button type="button" onClick={() => onPassword(usuario)} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-border px-3 font-semibold text-primary hover:border-secondary lg:min-h-10 lg:flex-none"><Icon name="key" className="h-4 w-4" />Restablecer contraseña</button>
      <button type="button" disabled={busy} onClick={() => onToggle(usuario)} className={`inline-flex min-h-11 flex-1 items-center justify-center rounded-xl border px-3 font-semibold disabled:opacity-50 lg:min-h-10 lg:flex-none ${usuario.activo ? 'border-danger/30 text-danger hover:bg-danger/5' : 'border-success/40 text-success-text hover:bg-success/5'}`}>{busy ? 'Guardando…' : usuario.activo ? 'Desactivar' : 'Activar'}</button>
    </div>
  )
}

export default function UsuariosPage() {
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [modal, setModal] = useState(null)
  const [busyId, setBusyId] = useState('')

  useEffect(() => {
    let active = true
    listarUsuarios()
      .then((result) => { if (active) setUsuarios(result) })
      .catch((loadError) => { if (active) setError(loadError.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  async function toggle(usuario) {
    const activar = !usuario.activo
    const question = activar
      ? `¿Activar el acceso de ${usuario.email}?`
      : `¿Desactivar el acceso de ${usuario.email}? No podrá ingresar a la aplicación hasta que se reactive.`
    if (!window.confirm(question)) return
    setBusyId(usuario.id); setError(''); setMessage('')
    try {
      await cambiarEstadoUsuario(usuario.id, activar)
      setUsuarios((current) => current.map((item) => item.id === usuario.id ? { ...item, activo: activar } : item))
      setMessage(activar ? 'Usuario activado correctamente.' : 'Usuario desactivado correctamente.')
    } catch (toggleError) { setError(toggleError.message) } finally { setBusyId('') }
  }

  function created(usuario) {
    setModal(null)
    setUsuarios((current) => [...current, usuario])
    setMessage('Usuario creado correctamente. Deberá cambiar la contraseña temporal en su primer ingreso.')
  }

  function passwordReset(usuarioId) {
    setModal(null)
    setUsuarios((current) => current.map((item) => item.id === usuarioId ? { ...item, debe_cambiar_clave: true } : item))
    setMessage('Contraseña temporal restablecida. El usuario deberá cambiarla en su próximo ingreso.')
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="text-sm font-semibold text-primary">Administración</p><h2 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Usuarios</h2><p className="mt-2 text-text-secondary">Administra las cuentas de acceso de la Directiva.</p></div>
        <button type="button" onClick={() => { setMessage(''); setModal({ type: 'nuevo' }) }} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-semibold text-white transition hover:bg-primary-hover"><Icon name="plus" />Nuevo usuario</button>
      </div>

      {message && <div className="rounded-xl border border-success/30 bg-success/10 p-4 text-sm font-medium text-success-text" role="status">{message}</div>}
      {error && <div className="rounded-xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{error}</div>}

      <section className="rounded-2xl border border-border bg-surface shadow-sm">
        {loading ? <div className="p-10 text-center text-sm text-text-secondary">Cargando usuarios…</div> : usuarios.length === 0 ? (
          <div className="px-5 py-14 text-center text-sm text-text-secondary">No hay usuarios registrados.</div>
        ) : (
          <>
            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-background text-xs uppercase tracking-wide text-text-secondary"><tr><th className="px-5 py-3 font-semibold">Correo</th><th className="px-5 py-3 font-semibold">Rol</th><th className="px-5 py-3 font-semibold">Estado</th><th className="px-5 py-3 font-semibold">Contraseña</th><th className="hidden px-5 py-3 font-semibold xl:table-cell">Fecha creación</th><th className="px-5 py-3 font-semibold">Último acceso</th><th className="px-5 py-3 text-right font-semibold">Acciones</th></tr></thead>
                <tbody className="divide-y divide-border">{usuarios.map((usuario) => <tr key={usuario.id} className="hover:bg-background"><td className="max-w-64 break-words px-5 py-4 font-medium">{usuario.email || '—'}</td><td className="whitespace-nowrap px-5 py-4 text-text-secondary">{roleLabels[usuario.rol] ?? usuario.rol}</td><td className="px-5 py-4"><EstadoBadge activo={usuario.activo} /></td><td className="px-5 py-4"><ClaveBadge pendiente={usuario.debe_cambiar_clave} /></td><td className="hidden whitespace-nowrap px-5 py-4 text-text-secondary xl:table-cell">{formatDate(usuario.created_at)}</td><td className="whitespace-nowrap px-5 py-4 text-text-secondary">{formatDate(usuario.last_sign_in_at, true)}</td><td className="px-5 py-4"><Acciones usuario={usuario} busy={busyId === usuario.id} onToggle={toggle} onPassword={(target) => { setMessage(''); setModal({ type: 'password', usuario: target }) }} /></td></tr>)}</tbody>
              </table>
            </div>
            <div className="divide-y divide-border lg:hidden">{usuarios.map((usuario) => (
              <article key={usuario.id} className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3"><div className="min-w-0"><h3 className="break-words font-semibold">{usuario.email || '—'}</h3><p className="mt-1 text-sm text-text-secondary">{roleLabels[usuario.rol] ?? usuario.rol}</p></div><EstadoBadge activo={usuario.activo} /></div>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm"><div className="col-span-2"><dt className="text-xs text-text-secondary">Contraseña</dt><dd className="mt-1"><ClaveBadge pendiente={usuario.debe_cambiar_clave} /></dd></div><div><dt className="text-xs text-text-secondary">Fecha creación</dt><dd className="mt-1 font-medium">{formatDate(usuario.created_at)}</dd></div><div><dt className="text-xs text-text-secondary">Último acceso</dt><dd className="mt-1 font-medium">{formatDate(usuario.last_sign_in_at, true)}</dd></div></dl>
                <div className="mt-4"><Acciones usuario={usuario} busy={busyId === usuario.id} onToggle={toggle} onPassword={(target) => { setMessage(''); setModal({ type: 'password', usuario: target }) }} /></div>
              </article>
            ))}</div>
          </>
        )}
      </section>

      {modal?.type === 'nuevo' && <NuevoUsuarioModal onClose={() => setModal(null)} onCreated={created} />}
      {modal?.type === 'password' && <RestablecerPasswordModal usuario={modal.usuario} onClose={() => setModal(null)} onDone={() => passwordReset(modal.usuario.id)} />}
    </div>
  )
}
